import { useCallback, useContext } from 'react';
import type { User } from '@esg/esg-global-types';

import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { UserContext } from '../../../context/UserContext';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import {
  softDeleteUser as softDeleteUserService,
  softDeleteUsers,
  restoreUsers,
  updateUser as updateUserService,
  updateUsersAccess,
  type BulkUserMutationEnvelope
} from '../../../graphql/services/user.service';
import { restoreUser as restoreUserV1, reAddUser } from '../../../lib/user_management/users';
import {
  sendNewUserWelcomeEmail,
  sendNewUserWelcomeEmailsBulk,
  type WelcomeEmailParams
} from '../../../lib/user_management/welcome_email';
import { RESEND_EMAIL_CHUNK_SIZE } from '../import/schemas';

/** Wrapper around user-management service calls with consistent snackbar feedback. */
export const useUserMutations = () => {
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);

  const notify = useCallback(
    (message: string, type: 'success' | 'error' | 'info' | 'warning') =>
      setFeedbackData({ message, state: true, type }),
    [setFeedbackData]
  );

  const summariseBulk = useCallback(
    (label: string, result: BulkUserMutationEnvelope) => {
      const parts: string[] = [];
      if (result.successes.length > 0) parts.push(`${result.successes.length} ${label}`);
      if (result.skipped.length > 0) parts.push(`${result.skipped.length} skipped`);
      if (result.failures.length > 0) parts.push(`${result.failures.length} failed`);
      const message = parts.length > 0 ? parts.join(' · ') : `No users ${label}`;
      let type: 'success' | 'warning' | 'error' = 'success';
      if (result.failures.length > 0) {
        type = result.successes.length > 0 ? 'warning' : 'error';
      }
      notify(message, type);
    },
    [notify]
  );

  const makeMetadata = useCallback(() => {
    if (!group || !company) {
      throw new Error('Group and company context required for mutation.');
    }
    return {
      group_id: group.id,
      company_id: company.id,
      audit_user_email: user?.email
    };
  }, [group, company, user]);

  const updateUser = useCallback(
    async (data: Partial<User> & { id: string }) => {
      try {
        const result = await updateUserService(makeMetadata(), data as never, {
          return_data: true,
          create_audit_log: true
        });
        notify(result ? 'Saved' : 'Save failed', result ? 'success' : 'error');
        return result;
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Save failed', 'error');
        return null;
      }
    },
    [makeMetadata, notify]
  );

  const softDeleteUser = useCallback(
    async (userId: string) => {
      try {
        const result = await softDeleteUserService(makeMetadata(), { id: userId });
        notify('User deleted', 'success');
        return result;
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Delete failed', 'error');
        return null;
      }
    },
    [makeMetadata, notify]
  );

  const restoreUser = useCallback(
    async (userId: string) => {
      try {
        // Re-enable the Firebase Auth account before un-deleting the Firestore
        // doc — delete disables Auth, and a Firestore-only restore leaves the
        // user unable to sign in (password resets cannot recover it).
        await reAddUser(userId);
        await restoreUserV1(userId);
        notify('User restored', 'success');
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Restore failed', 'error');
      }
    },
    [notify]
  );

  const resendInvite = useCallback(
    async (params: WelcomeEmailParams) => {
      try {
        await sendNewUserWelcomeEmail(params, 'resend_invite');
        notify('Invitation re-sent', 'success');
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Resend failed', 'error');
      }
    },
    [notify]
  );

  const bulkSoftDelete = useCallback(
    async (ids: string[]) => {
      try {
        const result = await softDeleteUsers(makeMetadata(), ids);
        summariseBulk('deleted', result);
        return result;
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Bulk delete failed', 'error');
        return null;
      }
    },
    [makeMetadata, summariseBulk, notify]
  );

  const bulkRestore = useCallback(
    async (ids: string[]) => {
      try {
        // Re-enable each user's Firebase Auth account before the Firestore
        // restore, mirroring the single-user path. Best-effort and chunked to
        // bound concurrency; a failure here means that user still cannot sign
        // in, so surface it rather than reporting a clean restore.
        let authFailures = 0;
        for (let i = 0; i < ids.length; i += RESEND_EMAIL_CHUNK_SIZE) {
          const slice = ids.slice(i, i + RESEND_EMAIL_CHUNK_SIZE);
          const settled = await Promise.allSettled(slice.map((id) => reAddUser(id)));
          authFailures += settled.filter((outcome) => outcome.status === 'rejected').length;
        }
        if (authFailures > 0) {
          notify(
            `${authFailures} user(s) could not be re-enabled for sign-in; a manual re-add may be needed.`,
            'warning'
          );
        }
        const result = await restoreUsers(makeMetadata(), ids);
        summariseBulk('restored', result);
        return result;
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Bulk restore failed', 'error');
        return null;
      }
    },
    [makeMetadata, summariseBulk, notify]
  );

  const bulkUpdateAccess = useCallback(
    async (args: {
      ids: string[];
      target_group_id: string;
      target_company_id: string;
      role: string;
    }) => {
      try {
        const result = await updateUsersAccess(makeMetadata(), args);
        summariseBulk('granted access', result);
        return result;
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Bulk grant failed', 'error');
        return null;
      }
    },
    [makeMetadata, summariseBulk, notify]
  );

  /**
   * Resends invitations in a single request per template bucket via
   * `sendNewUserWelcomeEmailsBulk` — no per-user fan-out, so no capacity 429s
   * (smoke finding B3). The per-email result is mapped back to the
   * {successes, failures, skipped} envelope by id (skipped = still within the
   * anti-spam cooldown from a prior successful invite).
   */
  const bulkResendInvite = useCallback(
    async (rows: Array<{ id: string } & WelcomeEmailParams>) => {
      try {
        const idByEmail = new Map(rows.map((row) => [row.email.trim().toLowerCase(), row.id]));
        const toId = (email: string): string | undefined =>
          idByEmail.get(email.trim().toLowerCase());
        const result = await sendNewUserWelcomeEmailsBulk(
          rows.map((row) => ({ name: row.name, email: row.email })),
          'bulk_resend_invite'
        );
        const envelope: BulkUserMutationEnvelope = {
          successes: result.sent_emails
            .map(toId)
            .filter((id): id is string => !!id)
            .map((id) => ({ id }) as unknown as User),
          failures: result.failed_emails
            .map((email) => ({ id: toId(email) ?? email, error: 'send failed' }))
            .filter((failure) => !!failure.id),
          skipped: result.skipped_cooldown_emails.map((email) => ({
            id: toId(email) ?? email,
            reason: 'recently invited'
          }))
        };
        summariseBulk('invitations sent', envelope);
        return envelope;
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Bulk resend failed', 'error');
        return null;
      }
    },
    [summariseBulk, notify]
  );

  return {
    updateUser,
    softDeleteUser,
    restoreUser,
    resendInvite,
    bulkSoftDelete,
    bulkRestore,
    bulkResendInvite,
    bulkUpdateAccess
  };
};
